import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import randomColor from "randomcolor";

import {
  Container,
  SignalsContent,
  SignalsHeaderContainer,
  SignalsTitle,
  SignalsCount,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  SecurityCell,
  DescriptionCell,
  SignalBadge,
  FilterNavContainer,
  FilterLabel,
  FilterButton,
  Dot,
  TableContainer,
  CloseButton,
  CloseButtonContainer,
} from "./SignalsTableStyles";

import { setHomeDisplay } from "../../../redux/slices/display";
import {
  selectLayerFilter,
  fetchSignals,
  setTableViewActiveFilterIndex,
  changeFilter,
  addSignal,
  setLeftPanelExpanded,
} from "../../../redux/slices/heatMap";

const COLUMNS = [
  { id: "security", label: "SECURITY", width: "12%" },
  { id: "name", label: "DESCRIPTION", width: "20%" },
  { id: "asset group", label: "TYPE", width: "10%" },
  { id: "status", label: "STATUS", width: "8%" },
  { id: "model_id", label: "MODEL", width: "15%" },
  { id: "interval", label: "INTERVAL", width: "6%" },
  { id: "entry_direction", label: "SIGNAL", width: "8%" },
  { id: "created_at", label: "DETECTED", width: "10%" },
];

const TICKER_COLUMNS = ["security", "name", "asset group"];

const SignalsTable = ({ signals }) => {
  const dispatch = useDispatch();

  const currentUtcTimestampMinutes = new Date().getTime() / 60000;

  const models = useSelector(({ models }) => models.data);
  const { isInitialLoad } = useSelector(({ display }) => display);

  const {
    filterValues,
    tableViewActiveFilterIndex,
    leftPanelExpanded,
    selectedSignals,
  } = useSelector(({ heatMap }) => heatMap);

  const modelIdToName = {};
  models.forEach((m) => {
    modelIdToName[m.id] = m.name;
  });

  const [signalsCount, setSignalsCount] = useState(0);
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  const filterButtons = useMemo(() => {
    const b = [
      "ALL SIGNALS",
      "OPEN ONLY",
      "EMERGING ONLY",
      "BUY SIGNALS",
      "SELL SIGNALS",
      "PATTERNS ONLY",
      "INDICATORS ONLY",
      "15M",
      "1H",
    ];
    const added = [];
    filterValues.tickers.forEach((t) => {
      const assetGroup = t["asset group"];

      if (!added.includes(assetGroup)) {
        b.push({ label: assetGroup, value: assetGroup });
        added.push(assetGroup);
      }
    });
    return b;
  }, [filterValues]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Sort the data based on current sort column and direction
  const signalsSorted = useMemo(() => {
    const sortedSignals = signals.filter(
      (s) => s?.signal_type !== "missing_signal"
    );
    setSignalsCount(sortedSignals.length);

    sortedSignals.sort((a, b) => {
      // Special handling for time-based "detected" column
      if (sortColumn === "created_at") {
        const timeA = Date.parse(a.created_at);
        const timeB = Date.parse(b.created_at);
        return sortDirection === "asc"
          ? timeA > timeB
            ? 1
            : -1
          : timeA < timeB
          ? 1
          : -1;
      }

      let valueA, valueB;

      if (TICKER_COLUMNS.includes(sortColumn)) {
        valueA = a.ticker[sortColumn];
        valueB = b.ticker[sortColumn];
      } else {
        valueA = a[sortColumn];
        valueB = b[sortColumn];
      }

      // String comparison (case-insensitive)
      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB, undefined, { sensitivity: "base" })
          : valueB.localeCompare(valueA, undefined, { sensitivity: "base" });
      }

      // Default comparison
      return sortDirection === "asc"
        ? valueA > valueB
          ? 1
          : -1
        : valueA < valueB
        ? 1
        : -1;
    });
    return sortedSignals;
  }, [signals, sortColumn, sortDirection]);

  useEffect(() => {
    if (!selectedSignals.length && isInitialLoad) {
      signalsSorted.forEach((s, index) => {
        if (index >= 5) {
          return;
        }
        const highlightColor = randomColor();
        console.log(s);
        dispatch(addSignal({ ...s, color: highlightColor }));
      });
    }
  }, [selectedSignals, dispatch, signalsSorted, isInitialLoad]);

  const getSortIcon = (column) => {
    if (column !== sortColumn) {
      return faSort;
    }
    return sortDirection === "asc" ? faSortUp : faSortDown;
  };

  const calculateSignalAgo = (created_at) => {
    const minutesDiff =
      currentUtcTimestampMinutes - Date.parse(created_at + "Z") / 60000;

    if (minutesDiff <= 5) {
      return "5 mins ago";
    }

    if (minutesDiff <= 15) {
      return "15 mins ago";
    }

    return ">30mins";
  };

  const handleRowClick = (index, rowData) => {
    setSelectedRowIndex(index === selectedRowIndex ? null : index);
    let highlightColor = randomColor();
    dispatch(addSignal({ ...rowData, color: highlightColor }));

    if (leftPanelExpanded !== 0) {
      dispatch(setLeftPanelExpanded(0));
    }
  };

  const getSignalType = (signal) => {
    if (signal?.status === "ACTIVE") {
      return "OPEN";
    }

    if (signal?.status === "FORMED") {
      return "EMERGING";
    }

    return "";
  };

  return (
    <Container>
      {isInitialLoad === true && (
        <CloseButtonContainer>
          <CloseButton
            onClick={() => {
              dispatch(setHomeDisplay());
            }}
          >
            ×
          </CloseButton>
        </CloseButtonContainer>
      )}

      <FilterNavContainer>
        <FilterLabel>Filter by:</FilterLabel>
        {filterButtons.map((button, index) => (
          <FilterButton
            key={index}
            active={
              tableViewActiveFilterIndex !== null &&
              tableViewActiveFilterIndex === index
            }
            onClick={() => {
              if (isInitialLoad === true) {
                dispatch(setHomeDisplay());
                return;
              }

              if (index !== null && index !== tableViewActiveFilterIndex) {
                const activeFilter = filterButtons[index];
                if (typeof activeFilter === "object") {
                  dispatch(
                    selectLayerFilter(
                      {
                        "asset group": {
                          values: [activeFilter],
                          isSelected: false,
                        },
                      },
                      true,
                      false
                    )
                  );
                  dispatch(
                    changeFilter(
                      {
                        status: null,
                        entryDirection: null,
                        signalType: null,
                        showMissingSignals: false,
                      },
                      false
                    )
                  );
                } else {
                  const resetLayeredFilters = {
                    intervals: {
                      values: [],
                      isSelected: false,
                    },
                    modelIds: {
                      values: [],
                      isSelected: false,
                    },
                  };

                  for (let tickerFieldIndex in filterValues.tickerFields) {
                    resetLayeredFilters[
                      filterValues.tickerFields[tickerFieldIndex]
                    ] = {
                      values: [],
                      isSelected: false,
                    };
                  }

                  const toggleFilters = {
                    status: null,
                    entryDirection: null,
                    signalType: null,
                    showMissingSignals: false,
                  };

                  if (activeFilter === "OPEN ONLY") {
                    toggleFilters.status = "ACTIVE";
                    toggleFilters.signalType = "pattern";
                  } else if (activeFilter === "EMERGING ONLY") {
                    toggleFilters.status = "FORMED";
                    toggleFilters.signalType = "pattern";
                  } else if (activeFilter === "BUY SIGNALS") {
                    toggleFilters.entryDirection = "BUY";
                  } else if (activeFilter === "SELL SIGNALS") {
                    toggleFilters.entryDirection = "SELL";
                  } else if (activeFilter === "PATTERNS ONLY") {
                    toggleFilters.signalType = "pattern";
                  } else if (activeFilter === "INDICATORS ONLY") {
                    toggleFilters.signalType = "indicator";
                  } else if (activeFilter === "15M") {
                    resetLayeredFilters.intervals.values.push({
                      label: "15m",
                      value: 15,
                    });
                  } else if (activeFilter === "1H") {
                    resetLayeredFilters.intervals.values.push({
                      label: "1h",
                      value: 60,
                    });
                  }
                  dispatch(selectLayerFilter(resetLayeredFilters, true, false));
                  dispatch(changeFilter(toggleFilters, false));
                }
                dispatch(setTableViewActiveFilterIndex(index));
                dispatch(fetchSignals());
              }
            }}
          >
            {typeof button === "object" ? (
              <>
                <Dot type={button.value} /> {button.label}
              </>
            ) : (
              button
            )}
          </FilterButton>
        ))}
      </FilterNavContainer>

      <SignalsContent>
        <SignalsHeaderContainer>
          <SignalsTitle>
            Trading Signals <SignalsCount>({signalsCount})</SignalsCount>
          </SignalsTitle>
        </SignalsHeaderContainer>

        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                {COLUMNS.map((column) => (
                  <TableHeader
                    key={column.id}
                    style={{ width: column.width }}
                    active={sortColumn === column.id}
                    sortable={true}
                    onClick={() => {
                      if (isInitialLoad === true) {
                        dispatch(setHomeDisplay());
                        return;
                      }
                      handleSort(column.id);
                    }}
                  >
                    {column.label}
                    <span className="sort-icon">
                      <FontAwesomeIcon icon={getSortIcon(column.id)} />
                    </span>
                  </TableHeader>
                ))}
              </tr>
            </TableHead>
            <TableBody>
              {signalsSorted.map((signal, index) => (
                <TableRow
                  key={index}
                  selected={selectedRowIndex === index}
                  onClick={() => handleRowClick(index, signal)}
                >
                  <SecurityCell>{signal.ticker.security}</SecurityCell>
                  <DescriptionCell>{signal.ticker.name}</DescriptionCell>
                  <TableCell>{signal.ticker["asset group"]}</TableCell>
                  <TableCell>{getSignalType(signal)}</TableCell>
                  <TableCell>
                    {signal.signal_type === "indicator"
                      ? signal.model_id
                      : modelIdToName[signal.model_id]}
                  </TableCell>
                  <TableCell>{signal.interval === 15 ? "15m" : "1h"}</TableCell>
                  <TableCell>
                    <SignalBadge
                      type={
                        signal?.entry_direction?.toLowerCase() ||
                        "missing_signal"
                      }
                    >
                      {signal?.entry_direction || "MISSING SIGNAL"}
                    </SignalBadge>
                  </TableCell>
                  <TableCell>{calculateSignalAgo(signal.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SignalsContent>
    </Container>
  );
};

export default SignalsTable;
